import { useAuth } from "@/core/components/auth/store/auth";
import { showFeedbackToast } from "@/core/components/ui/feedback-toast";
import { EVENTS, useActionsTrack } from "@/core/services/menu-analytics";
import { useTranslation } from "react-i18next";
import { useServiceManagementSso } from "./use-service-management-sso";

/**
 * PED-13066: abre a Gestão de Serviços (ex-Nexus) já autenticada, numa nova
 * aba — mesma UX das outras "migration rows" do menu (Explorar espaços).
 *
 * O redirect só acontece depois da troca de sessão responder; não há link
 * estático porque a URL carrega tokens de sessão de uso único.
 *
 * `origin` identifica de onde partiu o clique (sidebar vs. botão em Meus
 * Escritórios) — mesmo evento, categoria de tracking diferente, para medir
 * qual entrada é mais usada.
 */
export const useOpenServiceManagement = (
  origin: "MENU" | "MY_OFFICES" = "MENU",
) => {
  const { userInfo } = useAuth();
  const { onActionClick } = useActionsTrack();
  // `core`, não `my-offices`: este hook roda dentro da sidebar global
  // (useWosMenu), montada em toda página. `my-offices` só é carregado sob
  // demanda (namespace lazy, com Suspense) quando o usuário abre aquela
  // rota — usá-lo aqui suspendia a sidebar no boot, fora de uma transição.
  const [translate] = useTranslation(["core"]);
  const { mutate, isPending } = useServiceManagementSso();

  const openServiceManagement = () => {
    const companyId = userInfo?.user?.leagues_id?.[0];
    if (!companyId || isPending) return;

    mutate(companyId, {
      onSuccess: ({ redirectUrl }) => {
        // Tracker dispara só no sucesso real do handoff — um clique que falhou
        // antes de sair do WOS não é uma saída.
        onActionClick(
          "REDIRECT_NEXUS_TICKETS_CLICK" as keyof typeof EVENTS,
          origin,
          { company_id: companyId },
        );
        window.open(redirectUrl, "_blank", "noopener,noreferrer");
      },
      onError: () => {
        showFeedbackToast({
          variant: "red",
          title: translate("service-management-sso-error-title") as string,
          description: translate(
            "service-management-sso-error-description",
          ) as string,
        });
      },
    });
  };

  return { openServiceManagement, isPending };
};
